import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { computeGridCellValues } from '../utils/karmaCalc';

const VIEW_W = 600;
const VIEW_H = 700;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GridCellsRenderer = ({
  cells, results, day, month, year,
  title, gridBgImage, gridTitle, popupsEnabled = true,
  // Optional — enables d1_XXX / d2_XXX / sum_XXX formula tokens and the
  // `date_source: 'sum'` cell mode.
  results2 = null, day2 = null, month2 = null, year2 = null,
  resultsSum = null,
}) => {
  const [openCell, setOpenCell] = useState(null);

  const enabled = useMemo(
    () => (Array.isArray(cells) ? cells.filter((c) => c.enabled !== false) : []),
    [cells]
  );

  const computed = useMemo(
    () => computeGridCellValues(
      enabled, results, day, month, year,
      (results2 || resultsSum)
        ? { results2, day, month, year, day2, month2, year2, resultsSum }
        : null,
    ),
    [enabled, results, day, month, year, results2, day2, month2, year2, resultsSum]
  );

  if (!enabled.length && !gridBgImage && !gridTitle) return null;

  const titleReserve = gridTitle ? 60 : 0;
  const bounds = enabled.reduce(
    (acc, c) => ({
      minX: Math.min(acc.minX, c.x),
      minY: Math.min(acc.minY, c.y),
      maxX: Math.max(acc.maxX, c.x + c.w),
      maxY: Math.max(acc.maxY, c.y + c.h),
    }),
    { minX: VIEW_W, minY: VIEW_H, maxX: 0, maxY: 0 }
  );
  const padX = 20;
  const padY = 20;
  const hasCells = enabled.length > 0;
  const vbX = hasCells ? Math.max(0, bounds.minX - padX) : 0;
  const vbY = hasCells ? Math.max(0, bounds.minY - padY - titleReserve) : 0;
  const vbW = hasCells ? Math.max(100, bounds.maxX - vbX + padX) : VIEW_W;
  const vbH = hasCells ? Math.max(60 + titleReserve, bounds.maxY - vbY + padY) : 200;

  const openDesc = (cell, v1, v2) => {
    if (!popupsEnabled) return;
    if (!(cell.description || '').trim()) return;
    setOpenCell({ ...cell, __v1: v1, __v2: v2 });
  };

  return (
    <div className="karma-grid-section" data-testid="grid-cells-section">
      {title && <h3 className="karma-grid-title">{title}</h3>}
      <div className="karma-grid-wrap" style={{ maxWidth: 720, margin: '0 auto' }}>
        <svg
          viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        >
          {gridBgImage && (
            <image
              href={gridBgImage.startsWith('http') ? gridBgImage : `${BACKEND_URL}/api/uploads/${gridBgImage}`}
              x={vbX} y={vbY} width={vbW} height={vbH}
              preserveAspectRatio="xMidYMid slice"
              opacity="0.9"
              data-testid="grid-canvas-bgimage-render"
            />
          )}
          {gridTitle && (
            <text
              x={vbX + vbW / 2}
              y={vbY + 36}
              textAnchor="middle"
              fill="#fbbf24"
              fontSize="26"
              fontWeight="800"
              fontFamily="'Cinzel', serif"
              style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.85)', strokeWidth: 1.5 }}
              data-testid="grid-canvas-title-render"
            >
              {gridTitle}
            </text>
          )}
          {enabled.map((c) => {
            const r = computed[c.id] || {};
            const v1 = r.v1;
            const v2 = r.v2;
            const has1 = v1 !== undefined && v1 !== null;
            const has2 = v2 !== undefined && v2 !== null;
            const display = has1 && has2 ? `${v1} / ${v2}` : (has1 ? `${v1}` : (has2 ? `${v2}` : null));
            const isTitleOnly = c.source === 'none' && (c.source2 || 'none') === 'none';
            const hasDesc = !!(c.description || '').trim() && popupsEnabled;
            return (
              <g
                key={c.id}
                data-testid={`grid-cell-render-${c.id}`}
                onClick={() => openDesc(c, v1, v2)}
                style={{ cursor: hasDesc ? 'pointer' : 'default' }}
              >
                <rect
                  x={c.x}
                  y={c.y}
                  width={c.w}
                  height={c.h}
                  fill={c.color || '#1f2937'}
                  stroke={c.border_color || '#fbbf24'}
                  strokeWidth="1.5"
                  rx={4}
                  opacity="0.95"
                />
                {c.show_label !== false && c.label && (
                  <text
                    x={c.x + c.w / 2}
                    y={c.y + (isTitleOnly ? c.h / 2 : Math.max(14, c.h * 0.22))}
                    textAnchor="middle"
                    dominantBaseline={isTitleOnly ? 'central' : 'auto'}
                    fill={c.text_color || '#ffffff'}
                    fontSize={Math.max(11, Math.min(c.w, c.h) * (isTitleOnly ? 0.32 : 0.22))}
                    fontWeight={isTitleOnly ? '800' : '700'}
                    fontFamily="'Cinzel', serif"
                    style={{ pointerEvents: 'none', paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.6)', strokeWidth: 0.5 }}
                  >
                    {c.label}
                  </text>
                )}
                {display !== null && (
                  <text
                    x={c.x + c.w / 2}
                    y={c.y + c.h * (c.show_label !== false && c.label ? 0.66 : 0.55)}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={c.text_color || '#ffffff'}
                    fontSize={Math.max(13, Math.min(c.w, c.h) * (has1 && has2 ? 0.34 : 0.42))}
                    fontWeight="800"
                    fontFamily="'Inter', sans-serif"
                    style={{ pointerEvents: 'none', paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.65)', strokeWidth: 0.6 }}
                  >
                    {display}
                  </text>
                )}
                {/* Info dot (top-right corner) when description exists */}
                {hasDesc && (
                  <g style={{ pointerEvents: 'none' }}>
                    <circle cx={c.x + c.w - 8} cy={c.y + 8} r={5} fill="#fbbf24" opacity="0.9" />
                    <text x={c.x + c.w - 8} y={c.y + 8} textAnchor="middle" dominantBaseline="central"
                      fill="#0b0b0f" fontSize="7" fontWeight="900" fontFamily="serif">
                      i
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Description popup modal */}
      {openCell && (
        <div
          data-testid="grid-cell-desc-modal"
          onClick={() => setOpenCell(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(6px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 520,
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              background: 'linear-gradient(135deg, rgba(20,10,40,0.95), rgba(10,10,25,0.98))',
              border: '1px solid rgba(251,191,36,0.35)',
              borderRadius: 12,
              padding: 24,
              color: 'white',
              boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 40px rgba(251,191,36,0.15)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setOpenCell(null)}
              data-testid="grid-cell-desc-close"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: 8,
                width: 32,
                height: 32,
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={16} />
            </button>
            {openCell.label && (
              <h3 style={{
                fontFamily: "'Cinzel', serif",
                fontSize: 22,
                fontWeight: 800,
                color: '#fbbf24',
                marginBottom: 8,
                paddingRight: 40,
              }}>
                {openCell.label}
              </h3>
            )}
            {(openCell.__v1 !== null || openCell.__v2 !== null) && (
              <div style={{ fontSize: 32, fontWeight: 800, color: 'white', marginBottom: 16 }}>
                {openCell.__v1 !== null && openCell.__v2 !== null
                  ? `${openCell.__v1} / ${openCell.__v2}`
                  : (openCell.__v1 !== null ? openCell.__v1 : openCell.__v2)}
              </div>
            )}
            <div style={{
              fontSize: 15,
              lineHeight: 1.6,
              color: 'rgba(255,255,255,0.85)',
              whiteSpace: 'pre-wrap',
            }}>
              {openCell.description}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GridCellsRenderer;
